import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getNavItems, clearNavItemsCache, NavigationItems } from '@/services/supabase/navigation';

// Define cache times - using the same aggressive caching as other queries
const HOUR_IN_MS = 1000 * 60 * 60;
const DAY_IN_MS = HOUR_IN_MS * 24;
const STALE_TIME = DAY_IN_MS * 30; // 30 days stale time
const GC_TIME = DAY_IN_MS * 60; // 60 days garbage collection

// Define query keys for better cache management
export const navigationKeys = {
  all: ['navigation'] as const,
  byProject: (projectId: string | number) => 
    [...navigationKeys.all, 'byProject', String(projectId)] as const,
};

/**
 * Hook to fetch navigation items for a project
 * @param projectId The project ID to fetch navigation items for
 * @param options Additional options for the query
 */
export function useNavigationItems(
  projectId: string | number | undefined,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: navigationKeys.byProject(projectId || ''),
    queryFn: async () => {
      if (!projectId) return null;
      return getNavItems(projectId);
    },
    enabled: !!projectId && (options.enabled !== false),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Hook to clear navigation cache and refetch
 */
export function useClearNavigationCache() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectId: string | number) => {
      clearNavItemsCache(projectId);
      return projectId;
    },
    onSuccess: (projectId) => {
      // Invalidate the navigation query for this project
      queryClient.invalidateQueries({
        queryKey: navigationKeys.byProject(projectId),
      });
    },
  });
}
